import { FC, useMemo } from 'react';
import { Grid, Typography, Box, CircularProgress, Alert, Chip, Tooltip } from '@mui/material';
import { useGuardianState } from '../../../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useGuardianScheduleHook } from '../../../../hooks/useGuardianAcademicHook';
import PaginatedGrid from '../../../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { IScheduleResponse, IPeriodResponse, EWeekDay } from '../../../../../../../apis/academics/academicsSchedules.interfaces';

interface TimetableRow {
  id: string;
  className: string;
  [timeSlot: string]: string | number | IPeriodResponse; // Dynamic time slot columns + period data
}

const formatTimeSlot = (startTime: string, endTime: string): string => {
  try {
    const [startHour, startMin] = startTime.split(':').map(Number);

    const formatTime = (hour: number, min: number) => {
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${min.toString().padStart(2, '0')} ${period}`;
    };

    return `${formatTime(startHour, startMin)}`;
  } catch {
    return `${startTime}-${endTime}`;
  }
};

const GuardianTimetableView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId } = useGuardianState();

  const { data, isLoading, error } = useGuardianScheduleHook(
    selectedChildId || '',
    {
      page: 1,
      limit: 100, // Get all schedules for the child
    }
  );

  const schedules = useMemo(() => data?.schedules || [], [data?.schedules]);

  // Transform schedule data into timetable format
  const { rows, columns } = useMemo(() => {
    if (!schedules.length) {
      return { rows: [], columns: [] };
    }

    // Get all unique time slots from all schedules
    // Use timeSlot from API response if available, otherwise construct from startTime/endTime
    const timeSlots = new Set<string>();
    const periodsByTimeSlot = new Map<string, Map<string, IPeriodResponse>>();

    schedules.forEach((schedule: IScheduleResponse) => {
      schedule.periods?.forEach((period: IPeriodResponse) => {
        // Check if timeSlot exists in period (from API response)
        const timeSlot = (period as any).timeSlot || `${period.startTime}-${period.endTime}`;
        timeSlots.add(timeSlot);

        if (!periodsByTimeSlot.has(timeSlot)) {
          periodsByTimeSlot.set(timeSlot, new Map());
        }
        periodsByTimeSlot.get(timeSlot)!.set(period.dayOfWeek, period);
      });
    });

    // Sort time slots by start time
    const sortedTimeSlots = Array.from(timeSlots).sort((a, b) => {
      // Extract start time for sorting (handle both formats)
      const startA = a.includes('-') ? a.split('-')[0] : a.split(':')[0];
      const startB = b.includes('-') ? b.split('-')[0] : b.split(':')[0];
      return startA.localeCompare(startB);
    });

    // Create columns: Class + time slots
    const gridColumns: GridColDef[] = [
      {
        field: 'className',
        headerName: translateText('guardian.academics.timetable.columns.class'),
        width: 180,
        flex: 0,
      },
      ...sortedTimeSlots.map((timeSlot: string) => {
        // Format time slot for display - use timeSlot directly if it's already formatted, otherwise format from startTime/endTime
        const formattedTime = timeSlot.includes('-')
          ? formatTimeSlot(timeSlot.split('-')[0], timeSlot.split('-')[1])
          : timeSlot; // If timeSlot is already formatted from API, use it directly

        return {
          field: timeSlot,
          headerName: formattedTime,
          width: 150,
          flex: 0,
          renderCell: (params: GridRenderCellParams) => {
            const period = params.row[`${timeSlot}_period`] as IPeriodResponse | undefined;
            if (!period) return '-';

            if (period.isBreak) {
              return (
                <Tooltip title={translateText('guardian.academics.timetable.breakTypes.lunch') || 'Lunch'}>
                  <Chip
                    label="Lunch"
                    size="small"
                    color="default"
                    variant="outlined"
                  />
                </Tooltip>
              );
            }

            const courseName = period.course?.name || '-';
            const teacherName = period.teacher?.name || '';

            return (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" fontWeight="bold">{courseName}</Typography>
                    {teacherName && (
                      <Typography variant="caption">{translateText('guardian.academics.timetable.columns.teacher')}: {teacherName}</Typography>
                    )}
                    {period.room && (
                      <Typography variant="caption">{translateText('guardian.academics.timetable.columns.room')}: {period.room}</Typography>
                    )}
                  </Box>
                }
              >
                <Typography variant="body2">
                  {courseName}
                </Typography>
              </Tooltip>
            );
          },
        };
      }),
    ];

    // Create rows: one row per schedule/class
    const gridRows: TimetableRow[] = schedules.map((schedule: IScheduleResponse, index: number) => {
      const className = schedule.class?.name || schedule.name || `Schedule ${index + 1}`;
      const row: TimetableRow = {
        id: schedule.id,
        className,
      };

      // Fill time slot columns
      sortedTimeSlots.forEach((timeSlot: string) => {
        // For now, use Monday as default day (can be enhanced to show all days)
        const periodsForSlot = periodsByTimeSlot.get(timeSlot);
        if (periodsForSlot) {
          // Find period for this schedule and time slot
          // Match by timeSlot field if available, otherwise match by startTime-endTime
          const period = schedule.periods?.find((p: IPeriodResponse) => {
            const periodTimeSlot = (p as any).timeSlot || `${p.startTime}-${p.endTime}`;
            return periodTimeSlot === timeSlot && p.dayOfWeek === EWeekDay.MONDAY;
          });
          if (period) {
            row[timeSlot] = period.course?.name || '-';
            row[`${timeSlot}_period`] = period;
          } else {
            row[timeSlot] = '-';
          }
        } else {
          row[timeSlot] = '-';
        }
      });

      return row;
    });

    return { rows: gridRows, columns: gridColumns };
  }, [schedules, translateText]);

  if (!selectedChildId) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-timetable-container">
        <Grid item xs={12}>
          <Alert severity="info">{translateText('guardian.selectChild')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-timetable-container">
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {translateText('guardian.academics.timetable.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-timetable-container">
        <Grid item xs={12}>
          <Alert severity="error">{translateText('common.error')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (!schedules.length || !rows.length) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-timetable-container">
        <Grid item xs={12}>
          <Alert severity="info">{translateText('guardian.academics.timetable.noSchedule')}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-timetable-container">
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 2 }} data-testid="guardian-timetable-title">
          {translateText('guardian.academics.timetable.title')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <PaginatedGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          dataTestId="guardian-timetable-grid"
          serverSide={false}
          hideColumnsOnMobile={[]}
        />
      </Grid>
    </Grid>
  );
};

export default GuardianTimetableView;
